/**
 * Cómo se dicen las fechas en esta aplicación.
 *
 * Estaban escritas cuatro veces —la cola de tickets, el hilo, la ficha de quien
 * pregunta y la vitrina de agentes—, cada una con su formato y su idea de qué
 * omitir. Juntarlas no es prolijidad: la regla que las obliga a estar juntas es
 * **el año**.
 *
 * El año se calla cuando es el de hoy —nadie dice «15 de enero de 2026»
 * hablando de la semana pasada— y se dice cuando NO lo es, porque «15 ene»
 * sobre algo de hace catorce meses se lee como enero de este año. Esa decisión
 * hay que tomarla una vez y no cuatro, que es como se llegó a que una tarjeta
 * mostrara actividad vieja con cara de reciente.
 *
 * Lo que no se puede leer devuelve el suplente que pida quien llama: unas
 * pantallas dicen «—» en una ficha de datos y otras no dicen nada.
 */

const cache = new Map<string, Intl.DateTimeFormat>();

const formato = (opciones: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const clave = JSON.stringify(opciones);
  let hecho = cache.get(clave);
  if (!hecho) {
    hecho = new Intl.DateTimeFormat('es-CL', opciones);
    cache.set(clave, hecho);
  }
  return hecho;
};

const leer = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const fecha = new Date(iso);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

/** El año sólo se dibuja cuando no es éste. Ver el comentario de arriba. */
const conAño = (fecha: Date, opciones: Intl.DateTimeFormatOptions) =>
  fecha.getFullYear() === new Date().getFullYear()
    ? opciones
    : { ...opciones, year: 'numeric' as const };

const esHoy = (fecha: Date): boolean => {
  const hoy = new Date();
  return (
    fecha.getFullYear() === hoy.getFullYear() &&
    fecha.getMonth() === hoy.getMonth() &&
    fecha.getDate() === hoy.getDate()
  );
};

const DIA = { day: 'numeric', month: 'short' } as const;
const HORA = { hour: '2-digit', minute: '2-digit' } as const;

/** «15 ene» — y «15 ene 2025» si no es de este año. */
export const soloDia = (iso: string | null | undefined, vacio = ''): string => {
  const fecha = leer(iso);
  return fecha ? formato(conAño(fecha, DIA)).format(fecha) : vacio;
};

/** «15 ene, 14:32». */
export const diaYHora = (iso: string | null | undefined, vacio = ''): string => {
  const fecha = leer(iso);
  return fecha ? formato(conAño(fecha, { ...DIA, ...HORA })).format(fecha) : vacio;
};

/**
 * «14:32» si es de hoy; si no, con su día.
 *
 * Para un hilo, que se lee de corrido: repetir la fecha en cada mensaje de la
 * misma tarde es ruido, y el día de la semana sin fecha —lo que decía antes—
 * es una hora sin día.
 */
export const horaSiEsHoy = (iso: string | null | undefined, vacio = ''): string => {
  const fecha = leer(iso);
  if (!fecha) return vacio;
  return esHoy(fecha) ? formato(HORA).format(fecha) : diaYHora(iso, vacio);
};

/** «mar., 15 ene, 14:32» — para una ficha de datos, donde se lee de a una. */
export const diaLargo = (iso: string | null | undefined, vacio = ''): string => {
  const fecha = leer(iso);
  return fecha ? formato(conAño(fecha, { weekday: 'short', ...DIA, ...HORA })).format(fecha) : vacio;
};
